// Groups the chart of accounts under parent accounts for the Accounts screen.
// Same rules as ComplianceManagerWeb/src/features/chart-of-accounts/account-hierarchy.ts.

// listID -> parent listID, only for parents that exist in the list; loops are broken by treating those accounts as top-level.
export function validParents(accounts) {
  const byListId = new Map();
  accounts.forEach((a) => { if (a.listID) byListId.set(a.listID, a); });

  const parentOf = new Map();
  accounts.forEach((a) => {
    const parent = a.parentRefListID;
    if (a.listID && parent && parent !== a.listID && byListId.has(parent)) parentOf.set(a.listID, parent);
  });

  accounts.forEach((a) => {
    if (!a.listID) return;
    const seen = new Set([a.listID]);
    let next = parentOf.get(a.listID);
    while (next) {
      if (seen.has(next)) {
        parentOf.delete(a.listID);
        break;
      }
      seen.add(next);
      next = parentOf.get(next);
    }
  });
  return parentOf;
}

// Sort order matches the server: ascending puts empty values first.
export function compareBy(field, dir) {
  const sign = dir === 'D' ? -1 : 1;
  const time = (v) => (v ? new Date(v).getTime() || 0 : 0);
  return (a, b) => {
    let result;
    if (field === 'accountId') {
      result = a.accountID - b.accountID;
    } else if (field === 'lastReconciledTransactionDate' || field === 'reconciledThrough') {
      result = time(a[field]) - time(b[field]);
    } else {
      result = String(a[field] || '').localeCompare(String(b[field] || ''), undefined, { sensitivity: 'base', numeric: true });
    }
    return sign * (result || a.accountID - b.accountID);
  };
}

// Top-level accounts with everything under them, each family as a flat list of { account, depth, childCount }.
// A search keeps a match's parent and the match's own sub-accounts in view.
export function buildFamilies(accounts, parentOf, search, compare) {
  const children = new Map();
  accounts.forEach((a) => {
    const parent = a.listID ? parentOf.get(a.listID) : undefined;
    if (parent) children.set(parent, (children.get(parent) || []).concat(a));
  });

  const term = (search || '').trim().toLowerCase();
  let visible = null;
  if (term) {
    visible = new Set();
    const byListId = new Map();
    accounts.forEach((a) => { if (a.listID) byListId.set(a.listID, a); });
    const addDescendants = (a) => {
      (children.get(a.listID) || []).forEach((c) => {
        visible.add(c.accountID);
        addDescendants(c);
      });
    };
    accounts.forEach((a) => {
      if (!String(a.name || '').toLowerCase().includes(term)) return;
      visible.add(a.accountID);
      addDescendants(a);
      let up = a.listID ? parentOf.get(a.listID) : undefined;
      while (up) {
        const ancestor = byListId.get(up);
        if (!ancestor) break;
        visible.add(ancestor.accountID);
        up = parentOf.get(up);
      }
    });
  }

  const show = (a) => !visible || visible.has(a.accountID);
  const flatten = (a, depth, out) => {
    const kids = (children.get(a.listID) || []).filter(show).sort(compare);
    out.push({ account: a, depth, childCount: kids.length });
    kids.forEach((k) => flatten(k, depth + 1, out));
  };

  return accounts
    .filter((a) => !(a.listID && parentOf.has(a.listID)) && show(a))
    .sort(compare)
    .map((root) => {
      const rows = [];
      flatten(root, 0, rows);
      return rows;
    });
}

// QuickBooks refuses a chart deeper than this many levels (top-level = level 1). The API checks the same limit.
export const MAX_LEVELS = 5;

function levelOf(listId, parentOf) {
  let level = 1;
  const seen = new Set([listId]);
  let next = parentOf.get(listId);
  while (next && !seen.has(next)) {
    seen.add(next);
    level++;
    next = parentOf.get(next);
  }
  return level;
}

// Levels in the account's own branch: 1 when it has no sub-accounts.
function heightOf(listId, childrenOf, path) {
  const seen = path || new Set([listId]);
  let best = 0;
  (childrenOf.get(listId) || []).forEach((c) => {
    if (seen.has(c)) return;
    seen.add(c);
    best = Math.max(best, heightOf(c, childrenOf, seen));
    seen.delete(c);
  });
  return best + 1;
}

// Accounts the Parent Account dropdown can offer: active, same account type, not the account itself or one of its descendants,
// and within QuickBooks' level limit. A sub-account may be a parent. The API checks the same rules again on save.
export function eligibleParents(account, accounts, parentOf) {
  const isDescendant = (ancestor, candidate) => {
    const seen = new Set();
    let next = candidate;
    while (next && !seen.has(next)) {
      seen.add(next);
      next = parentOf.get(next);
      if (next === ancestor) return true;
    }
    return false;
  };
  const childrenOf = new Map();
  parentOf.forEach((parent, child) => childrenOf.set(parent, (childrenOf.get(parent) || []).concat(child)));
  const height = account.listID ? heightOf(account.listID, childrenOf) : 1;
  return accounts
    .filter((a) => a.accountID !== account.accountID && a.listID && a.isActive !== false && a.accountType === account.accountType
      && !(account.listID && isDescendant(account.listID, a.listID))
      && levelOf(a.listID, parentOf) + height <= MAX_LEVELS)
    .sort((x, y) => String(x.name || '').localeCompare(String(y.name || ''), undefined, { sensitivity: 'base' }));
}

// Option text per listID, as in QuickBooks: the account number first (when set), then the name.
export function parentOptionLabels(options) {
  const labels = new Map();
  options.forEach((o) => {
    if (o.listID) labels.set(o.listID, o.accountNumber ? o.accountNumber + ' ' + o.name : o.name);
  });
  return labels;
}

// Hint per listID, as in QuickBooks: the account type, or "Sub of <parent>" for a sub-account.
export function parentOptionDetails(options, accounts, parentOf) {
  const nameOf = new Map();
  accounts.forEach((a) => { if (a.listID) nameOf.set(a.listID, a.name); });
  const details = new Map();
  options.forEach((o) => {
    if (!o.listID) return;
    const parent = parentOf.get(o.listID);
    details.set(o.listID, parent && nameOf.get(parent) ? 'Sub of ' + nameOf.get(parent) : (o.accountType || ''));
  });
  return details;
}
